import React from 'react';
import { motion } from 'framer-motion';
import { FaRobot, FaGamepad, FaMicrochip } from 'react-icons/fa';

function About() {
  const passions = [
    { icon: FaRobot, text: 'Learning AI', emoji: '🧠' },
    { icon: FaGamepad, text: 'Designing Games', emoji: '🎮' },
    { icon: FaMicrochip, text: 'Controlling Things with IoT', emoji: '⚡' },
  ];

  return (
    <div className="w-full min-h-screen" style={{ background: 'linear-gradient(to bottom right, #3B0764, #6B21A8, #7C3AED, #8B5CF6)' }}>
      {/* Header */}
      <section className="py-0 md:py-0 px-4" style={{ background: 'linear-gradient(to bottom, rgba(59,7,100,0.9), rgba(107,33,168,0.9))' }}>
        <motion.div
          className="container mx-auto max-w-3xl text-center py-16 md:py-20"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1 className="text-5xl md:text-6xl font-black text-white mb-4 font-heading" style={{ fontFamily: "'Bangers', cursive", color: '#FDE047', WebkitTextStroke: '1px #0F0A1A', textShadow: '3px 3px 0 #0F0A1A' }}>
            About Me 👋
          </h1>
          <p className="text-xl text-white max-w-2xl mx-auto" style={{ fontFamily: "'Fredoka One', cursive", color: '#FDE047', WebkitTextStroke: '0.5px #0F0A1A' }}>
            A young creator from the USA building amazing AI and IoT projects!
          </p>
        </motion.div>
      </section>

      {/* Main Content */}
      <section className="py-16 md:py-24 px-4" style={{ background: 'linear-gradient(to bottom, rgba(107,33,168,0.9), rgba(123,58,237,0.9))' }}>
        <div className="container mx-auto max-w-4xl">
          <motion.div
            className="mb-24"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            {/* Story */}
            <div className="max-w-3xl mx-auto">
              <h2 className="text-4xl font-black text-white mb-6 font-heading" style={{ fontFamily: "'Bangers', cursive", color: '#FDE047', WebkitTextStroke: '0.5px #0F0A1A' }}>
                My Story
              </h2>
              <p className="text-xl text-white mb-4 leading-relaxed" style={{ fontFamily: "'Comic Neue', cursive", color: '#FFFBF0', textShadow: '1px 1px 2px rgba(0,0,0,0.5)' }}>
                I started building games when I was 6 years old! My passion is creating fun, interactive AI-powered games using Raspberry Pi, NVIDIA Jetson, and IoT sensors.
              </p>
              <p className="text-xl text-white mb-4 leading-relaxed" style={{ fontFamily: "'Comic Neue', cursive", color: '#FFFBF0', textShadow: '1px 1px 2px rgba(0,0,0,0.5)' }}>
                I love sharing my projects with everyone because I think AI is the future and most things will be controlled by AI. Learning it now means we'll all be ready for what's coming!
              </p>
              <p className="text-xl text-white leading-relaxed" style={{ fontFamily: "'Comic Neue', cursive", color: '#FFFBF0', textShadow: '1px 1px 2px rgba(0,0,0,0.5)' }}>
                Whether it's a motion-detection game, an AI-powered robot, or something completely new — I'm always excited to build, break, fix, and share with my friends! 🚀
              </p>
            </div>
          </motion.div>

          {/* Achievements */}
          <motion.div
            className="py-12"
            style={{ borderTop: '3px solid #0F0A1A' }}
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <h3 className="text-3xl font-black text-white text-center mb-12 font-heading" style={{ fontFamily: "'Bangers', cursive", color: '#FDE047', WebkitTextStroke: '0.5px #0F0A1A' }}>
              What I Love 💖
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
              {passions.map((passion, index) => {
                const IconComponent = passion.icon;
                return (
                  <motion.div
                    key={index}
                    whileHover={{ y: -4, rotate: 2 }}
                    className="bg-white border-3 border-black p-8 rounded-lg text-center hover:border-gray-300 hover:shadow-md transition-all"
                    style={{ boxShadow: '3px 3px 0 #0F0A1A' }}
                  >
                    <div className="text-4xl mb-3">{passion.emoji}</div>
                    <div className="text-3xl mb-4 text-purple-600 flex justify-center">
                      <IconComponent />
                    </div>
                    <p className="text-xl font-bold text-gray-900" style={{ fontFamily: "'Fredoka One', cursive" }}>
                      {passion.text}
                    </p>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>

          {/* What's Next */}
          <motion.div
            className="mt-24 p-8 md:p-12 rounded-lg border-3 border-black"
            style={{ background: 'linear-gradient(to bottom right, rgba(59,7,100,0.8), rgba(107,33,168,0.8))', boxShadow: '4px 4px 0 #0F0A1A' }}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h3 className="text-3xl font-black text-white mb-4 font-heading" style={{ fontFamily: "'Bangers', cursive", color: '#FDE047', WebkitTextStroke: '0.5px #0F0A1A' }}>
              What's Next? 🚀
            </h3>
            <p className="text-xl text-white mb-4 leading-relaxed" style={{ fontFamily: "'Comic Neue', cursive", color: '#FFFBF0', textShadow: '1px 1px 2px rgba(0,0,0,0.5)' }}>
              I'm constantly working on new and exciting projects! From AI-powered games to interactive robots, there's always something new cooking in my maker space.
            </p>
            <p className="text-xl text-white leading-relaxed" style={{ fontFamily: "'Comic Neue', cursive", color: '#FFFBF0', textShadow: '1px 1px 2px rgba(0,0,0,0.5)' }}>
              Make sure to check out my games section and follow me on social media to see all the cool stuff I'm creating! 🌟
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

export default About;
